import { useAuthStore } from "@/stores/authStore";
import router from '@/router';
import { isPlainObject } from "lodash-es";
import { API_BASE_URL, isFirebase, logVerbose } from "@/utils/config";

/**
 * Debug headers for local troubleshooting/dev
 * @param {Headers} headers
 * @return {Object} 
 */
function debugHeaders(headers) {
  return Object.fromEntries(
    Array.from(headers.entries()).map(([k, v]) => {
      // let converted = v;
      if (v.startsWith('Bearer ')) v = `${v.substring(0, 9)}...`;
      else if (k !== 'content-type') {
        if (v.length > 15) v = `${v.substring(0, 15)}...`;
      }
      return [k, v];
    })
  );
}

/**
 * Wraps the global fetch function to automatically include
 * the JWT from the authStore for authenticated requests.
 * @param {string} url The API full path endpoint (e.g., '/devices').
 * @param {Object} options The standard fetch options.
 * @returns {Promise<object>}
 */
export async function apiFetch(url, options = {}) {
  localStorage.setItem('redirect_after_login', router.currentRoute.value.fullPath);
    
  try {
    const endpoint = url.replace(API_BASE_URL, '');
    // Ensure Pinia is active when the component/action calls this function.
    const auth = useAuthStore();
    const { headers = {}, params, ...otherOptions } = options;
    let query = '';
    if (params && Object.keys(params).length > 0) {
      const queryString = new URLSearchParams();
      for (const [key, value] of Object.entries(options.params)) {
        if (value !== undefined && value !== null) {
          // If value is an object (e.g. next cursor), stringify it
          const val = typeof value === 'object' ? JSON.stringify(value) : value;
          queryString.append(key, val);
        }
      }
      query += (endpoint.includes('?') ? '&' : '?') + queryString.toString();
  5  }
    // Apply credentials for IAP (or CORS)
    const fetchOptions = {
      credentials: 'include',
      ...otherOptions,
    };
    if (!fetchOptions.headers) fetchOptions.headers = new Headers(headers);
    // Apply API auth header
    if (auth.jwt && !fetchOptions.headers.has('Authorization')) {
      fetchOptions.headers.set('Authorization', `Bearer ${auth.jwt}`);
    }
    if (options.body) {
      if (!(options.body instanceof FormData)) {
      // Convert body
        fetchOptions.headers.set('Content-Type', 'application/json');
        if (isPlainObject(options.body) || Array.isArray(options.body)) {
          fetchOptions.body = JSON.stringify(options.body);
        } else {
          throw new Error(`Unsupported body format`);
        }
      }
    }    
    if (logVerbose) {
      console.debug(`Calling ${fetchOptions.method || 'GET'} ${url + query}` +
        `\n\t${JSON.stringify(debugHeaders(fetchOptions.headers))}`);
    }
    const res = await fetch(url + query, fetchOptions);
    if (res.ok) {
      try {
        const data = await res.json();
        return data;
      } catch {
        return true;
      }
    }

    if (res.status === 401) {
      auth.logout('Session expired');
      if (isFirebase) {
        window.location.reload();
      } else {
        router.push({
          name: 'Login',
          query: { type: 401, from: router.currentRoute.value.fullPath },
        });
      }
      throw new Error('Unauthorized');
    }
    
    if (res.status === 403) {
      if (auth.userName) {
        console.warn(`User ${auth.userName} (${auth.role})` +
          ` denied access to ${endpoint}`);
      }
      router.push({
        name: 'AccessDenied',
        query: { type: 403, from: router.currentRoute.value.fullPath },
      });
      throw new Error('Forbidden');
    }

    const errorData = await res.json().catch(() => ({}));
    let errMsg = `HTTP ${res.status} (${endpoint})`
    if (errorData.message) errMsg += `: ${errorData.message}`;
    throw new Error(errMsg);
  } catch (err) {
    const isNetworkError = err instanceof TypeError || err.name === 'TypeError';
    if (isNetworkError) {
      console.error("Connection Refused or Network Down:", err);
      // Optional: Redirect to a "Service Down" page instead of Access Denied
      router.push({
        name: 'ServiceInterruption',
        query: { type: null, from: router.currentRoute.value.fullPath },
      }); 
      throw new Error('Service Interruption'); 
    }
    // Don't re-throw if we already handled the redirect for Auth/Forbidden
    if (['Unauthorized', 'Forbidden'].includes(err.message)) {
      return; 
    }
    // For everything else, throw so the component can show a toast/alert
    throw err;
  }
}

/**
 * Upload a file, compressing when large and allowing optional JSON metadata
 * @param {string} endpoint 
 * @param {File} file 
 * @param {Record<string, any>} [meta] 
 * @returns {Promise<any>}
 */
export async function uploadFile(endpoint, file, meta = {}) {
  const MAX_UNCOMPRESSED = 2 * 1024;
  const compress = file.size > MAX_UNCOMPRESSED;
  const { tag = 'file', ...fileMeta } = meta;
  
  const formData = new FormData();
  if (compress) {
    console.debug(`Compressing ${file.name}`);
    const fileStream = file.stream();
    // Compress the stream via browser-native Gzip CompressionStream API
    const compressionStream = new CompressionStream('gzip');
    const compressedStream = fileStream.pipeThrough(compressionStream);
    // Collect compressed data chunks back into a Blob
    const responseStream = new Response(compressedStream);
    const compressedBlob = await responseStream.blob();
    formData.append(tag, compressedBlob, `${file.name}.gz`);
  } else {
    formData.append(tag, file, file.name);
  }
  for (const k in fileMeta) {
    formData.append(k, fileMeta[k]);
  }

  const result = await apiFetch(endpoint, {
    method: 'POST',
    body: formData,
  });
  return result;
}
